import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useData } from '../contexts/DataContext';
import { useSettings } from '../contexts/SettingsContext';
import { 
  Plus, Search, Filter, Calendar, DollarSign, Building2, User, 
  Package, Clock, CheckCircle, XCircle, Edit2, Trash2, X, Save,
  TrendingUp, AlertCircle, ChevronDown, ChevronRight, FileText
} from 'lucide-react';
import { toast } from 'sonner';
import * as api from '../services/api';
import Tooltip from '../components/ui/Tooltip';

const Deals = () => {
  const navigate = useNavigate();
  const { state } = useData();
  const { settings } = useSettings();
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingDeal, setEditingDeal] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [services, setServices] = useState([]);
  const [leads, setLeads] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [dealsSummary, setDealsSummary] = useState(null);
  
  const primaryColor = settings?.primaryColor || '#00b30c';
  const currency = settings?.currency || 'COP';

  // Form state
  const [formData, setFormData] = useState({
    leadId: '',
    leadName: '',
    company: '',
    email: '',
    phone: '',
    services: [], // [{serviceId, name, value, quantity}]
    totalValue: 0,
    estimatedStartDate: '',
    estimatedCloseDate: '',
    actualCloseDate: '',
    status: 'negotiation', // negotiation, won, lost
    notes: '',
    assignedTo: '',
  });

  // Load data on mount only
  useEffect(() => {
    loadDeals(1);
    loadServices();
    loadLeads();
  }, []);

  const loadDeals = async (pageNum = 1) => {
    setLoading(true);
    try {
      const data = await api.getDeals(pageNum, 50, statusFilter, searchQuery);
      setDeals(data.deals || []);
      // Use server-side summary stats if available
      if (data.summary) {
        setDealsSummary(data.summary);
      }
      setTotalPages(data.pages || 1);
      setPage(pageNum);
    } catch (error) {
      console.error('Error loading deals:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadServices = async () => {
    try {
      const data = await api.getServices();
      setServices(data || []);
    } catch (error) {
      console.error('Error loading services:', error);
    }
  };

  const loadLeads = async () => {
    try {
      // Get leads that are in negotiation or closed stages
      const response = await api.getLeadsPaginated(1, 500);
      setLeads(response.leads || []);
    } catch (error) {
      console.error('Error loading leads:', error);
    }
  };

  // Reload deals when filters change
  useEffect(() => {
    const timer = setTimeout(() => {
      loadDeals(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, statusFilter]);

  // Format currency with tooltip
  const formatCurrency = (value, showFull = false) => {
    const fullValue = new Intl.NumberFormat('es-CO', {
      style: 'decimal',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value || 0) + ' COP';

    if (showFull || value < 1000000) {
      return fullValue;
    }

    const short = value >= 1000000000 
      ? (value / 1000000000).toFixed(1) + 'B COP'
      : (value / 1000000).toFixed(1) + 'M COP';

    return (
      <Tooltip content={fullValue}>
        <span className="cursor-help border-b border-dotted border-current">{short}</span>
      </Tooltip>
    );
  };

  // Use deals directly - already filtered from server
  const filteredDeals = deals;

  // Stats from summary or calculate from current deals
  const totalEstimated = dealsSummary?.totalValue || deals.reduce((sum, d) => sum + (d.totalValue || 0), 0);
  const totalWon = dealsSummary?.wonValue || deals.filter(d => d.status === 'won').reduce((sum, d) => sum + (d.totalValue || 0), 0);
  const totalLost = dealsSummary?.lostValue || deals.filter(d => d.status === 'lost').reduce((sum, d) => sum + (d.totalValue || 0), 0);
  const inNegotiation = dealsSummary?.negotiation || deals.filter(d => d.status === 'negotiation').length;

  // Handle form
  const handleOpenModal = (deal = null) => {
    if (deal) {
      setEditingDeal(deal);
      setFormData({
        leadId: deal.leadId || '',
        leadName: deal.leadName || '',
        company: deal.company || '',
        email: deal.email || '',
        phone: deal.phone || '',
        services: deal.services || [],
        totalValue: deal.totalValue || 0,
        estimatedStartDate: deal.estimatedStartDate?.split('T')[0] || '',
        estimatedCloseDate: deal.estimatedCloseDate?.split('T')[0] || '',
        actualCloseDate: deal.actualCloseDate?.split('T')[0] || '',
        status: deal.status || 'negotiation',
        notes: deal.notes || '',
        assignedTo: deal.assignedTo || '',
      });
    } else {
      setEditingDeal(null);
      setFormData({
        leadId: '',
        leadName: '',
        company: '',
        email: '',
        phone: '',
        services: [],
        totalValue: 0,
        estimatedStartDate: '',
        estimatedCloseDate: '',
        actualCloseDate: '',
        status: 'negotiation',
        notes: '',
        assignedTo: '',
      });
    }
    setShowModal(true);
  };

  const handleLeadSelect = (leadId) => {
    const lead = leads.find(l => l.id === leadId);
    if (lead) {
      setFormData(prev => ({
        ...prev,
        leadId: lead.id,
        leadName: lead.name,
        company: lead.company || '',
        email: lead.email || '',
        phone: lead.phone || '',
      }));
    }
  };

  const addService = (serviceId) => {
    const service = services.find(s => s.id === serviceId);
    if (service && !formData.services.find(s => s.serviceId === serviceId)) {
      const newService = {
        serviceId: service.id,
        name: service.name,
        value: service.minPrice || 0,
        quantity: 1,
      };
      const newServices = [...formData.services, newService];
      const total = newServices.reduce((sum, s) => sum + (s.value * s.quantity), 0);
      setFormData(prev => ({ ...prev, services: newServices, totalValue: total }));
    }
  };

  const updateServiceValue = (index, field, value) => {
    const newServices = [...formData.services];
    if (field === 'value') {
      // Parse currency formatted string to number
      const numericValue = typeof value === 'string' ? parseInt(value.replace(/[^\d]/g, '')) || 0 : value;
      newServices[index][field] = numericValue;
    } else if (field === 'quantity') {
      newServices[index][field] = parseInt(value) || 1;
    } else {
      newServices[index][field] = value;
    }
    const total = newServices.reduce((sum, s) => sum + (s.value * s.quantity), 0);
    setFormData(prev => ({ ...prev, services: newServices, totalValue: total }));
  };

  // Format number as currency for display in input
  const formatNumberAsCurrency = (value) => {
    if (!value && value !== 0) return '';
    return new Intl.NumberFormat('es-CO').format(value);
  };

  const removeService = (index) => {
    const newServices = formData.services.filter((_, i) => i !== index);
    const total = newServices.reduce((sum, s) => sum + (s.value * s.quantity), 0);
    setFormData(prev => ({ ...prev, services: newServices, totalValue: total }));
  };

  const handleSubmit = async () => {
    if (!formData.leadName.trim()) {
      toast.error('El nombre del cliente es requerido');
      return;
    }

    try {
      if (editingDeal) {
        await api.updateDeal(editingDeal.id, formData);
        toast.success('Negocio actualizado');
      } else {
        await api.createDeal(formData);
        toast.success('Negocio creado');
      }
      setShowModal(false);
      loadDeals();
    } catch (error) {
      toast.error('Error al guardar negocio');
    }
  };

  const handleDelete = async (dealId) => {
    try {
      await api.deleteDeal(dealId);
      toast.success('Negocio eliminado');
      setShowDeleteConfirm(null);
      loadDeals();
    } catch (error) {
      toast.error('Error al eliminar negocio');
    }
  };

  const handleStatusChange = async (dealId, newStatus) => {
    try {
      const deal = deals.find(d => d.id === dealId);
      const updateData = { 
        ...deal, 
        status: newStatus,
        actualCloseDate: newStatus === 'won' ? new Date().toISOString() : deal.actualCloseDate
      };
      await api.updateDeal(dealId, updateData);
      toast.success(`Negocio marcado como ${newStatus === 'won' ? 'Ganado' : newStatus === 'lost' ? 'Perdido' : 'En Negociación'}`);
      loadDeals();
    } catch (error) {
      toast.error('Error al actualizar estado');
    }
  };

  const statusColors = {
    negotiation: 'bg-yellow-100 text-yellow-700',
    won: 'bg-green-100 text-green-700',
    lost: 'bg-red-100 text-red-700',
  };

  const statusLabels = {
    negotiation: 'En Negociación',
    won: 'Ganado',
    lost: 'Perdido',
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: primaryColor }}></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Negocios</h1>
          <p className="text-slate-500 mt-1">Gestiona tus oportunidades de venta y cierres</p>
        </div>
        
        <button
          onClick={() => handleOpenModal()}
          className="flex items-center gap-2 px-4 py-2 text-white rounded-lg hover:opacity-90 transition-all"
          style={{ backgroundColor: primaryColor }}
        >
          <Plus className="w-5 h-5" />
          Nuevo Negocio
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-50">
              <TrendingUp className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Estimado Total</p>
              <p className="text-lg font-bold text-slate-900">{formatCurrency(totalEstimated)}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-green-50">
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Ganado</p>
              <p className="text-lg font-bold text-green-600">{formatCurrency(totalWon)}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-red-50">
              <XCircle className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Perdido</p>
              <p className="text-lg font-bold text-red-600">{formatCurrency(totalLost)}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-yellow-50">
              <Clock className="w-5 h-5 text-yellow-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">En Negociación</p>
              <p className="text-lg font-bold text-yellow-600">{inNegotiation} negocios</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
          <input
            type="text"
            placeholder="Buscar negocios..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 bg-white"
        >
          <option value="">Todos los estados</option>
          <option value="negotiation">En Negociación</option>
          <option value="won">Ganados</option>
          <option value="lost">Perdidos</option>
        </select>
      </div>

      {/* Deals List */}
      {filteredDeals.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
          <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-slate-900 mb-2">No hay negocios</h3>
          <p className="text-slate-500 mb-4">Crea tu primer negocio para comenzar a trackear oportunidades</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredDeals.map(deal => (
            <div 
              key={deal.id}
              className="bg-white rounded-xl border border-slate-200 p-5 hover:shadow-lg transition-shadow"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <span className={`px-2.5 py-1 text-xs font-medium rounded-full ${statusColors[deal.status]}`}>
                      {statusLabels[deal.status]}
                    </span>
                    <h3 className="text-lg font-semibold text-slate-900">{deal.company || deal.leadName}</h3>
                  </div>
                  
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                    <div>
                      <p className="text-slate-500">Empresa</p>
                      {deal.empresaId ? (
                        <button 
                          onClick={() => navigate(`/empresa/${deal.empresaId}`)}
                          className="font-medium text-indigo-600 hover:text-indigo-800 hover:underline text-left"
                        >
                          {deal.empresaName || deal.company || '-'}
                        </button>
                      ) : (
                        <p className="font-medium text-slate-900">{deal.company || '-'}</p>
                      )}
                    </div>
                    <div>
                      <p className="text-slate-500">Contacto</p>
                      <p className="font-medium text-slate-900">{deal.leadName || '-'}</p>
                    </div>
                    <div>
                      <p className="text-slate-500">Valor Total</p>
                      <p className="font-bold text-lg" style={{ color: primaryColor }}>{formatCurrency(deal.totalValue)}</p>
                    </div>
                    <div>
                      <p className="text-slate-500">Cierre Estimado</p>
                      <p className="font-medium text-slate-900">
                        {deal.estimatedCloseDate ? new Date(deal.estimatedCloseDate).toLocaleDateString('es-ES') : '-'}
                      </p>
                    </div>
                    <div>
                      <p className="text-slate-500">Servicios</p>
                      <p className="font-medium text-slate-900">{deal.services?.length || 0} servicios</p>
                    </div>
                  </div>

                  {deal.services?.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {deal.services.map((s, i) => (
                        <span key={i} className="px-2 py-1 bg-slate-100 text-slate-600 rounded text-xs">
                          {s.name}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  {deal.status === 'negotiation' && (
                    <>
                      <button
                        onClick={() => handleStatusChange(deal.id, 'won')}
                        className="p-2 hover:bg-green-50 rounded-lg transition-colors"
                        title="Marcar como Ganado"
                      >
                        <CheckCircle className="w-5 h-5 text-green-500" />
                      </button>
                      <button
                        onClick={() => handleStatusChange(deal.id, 'lost')}
                        className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                        title="Marcar como Perdido"
                      >
                        <XCircle className="w-5 h-5 text-red-500" />
                      </button>
                    </>
                  )}
                  <button
                    onClick={() => handleOpenModal(deal)}
                    className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                  >
                    <Edit2 className="w-4 h-4 text-slate-500" />
                  </button>
                  <button
                    onClick={() => setShowDeleteConfirm(deal.id)}
                    className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between bg-white rounded-xl border border-slate-200 px-4 py-3">
          <div className="text-sm text-slate-600">
            Página {page} de {totalPages}
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => loadDeals(page - 1)}
              disabled={page <= 1}
              className={`px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
                page <= 1 
                  ? 'border-slate-200 text-slate-400 cursor-not-allowed'
                  : 'border-slate-200 text-slate-700 hover:bg-slate-50'
              }`}
            >
              Anterior
            </button>
            <button
              onClick={() => loadDeals(page + 1)}
              disabled={page >= totalPages}
              className={`px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
                page >= totalPages
                  ? 'border-slate-200 text-slate-400 cursor-not-allowed'
                  : 'border-slate-200 text-slate-700 hover:bg-slate-50'
              }`}
            >
              Siguiente
            </button>
          </div>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-slate-200 sticky top-0 bg-white">
              <h2 className="text-xl font-semibold text-slate-900">
                {editingDeal ? 'Editar Negocio' : 'Nuevo Negocio'}
              </h2>
              <button onClick={() => setShowModal(false)} className="p-2 hover:bg-slate-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Lead Selection */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Seleccionar Lead Existente (opcional)
                </label>
                <select
                  value={formData.leadId}
                  onChange={(e) => handleLeadSelect(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 bg-white"
                >
                  <option value="">-- Seleccionar o crear nuevo --</option>
                  {leads.map(lead => (
                    <option key={lead.id} value={lead.id}>
                      {lead.name} - {lead.company || 'Sin empresa'}
                    </option>
                  ))}
                </select>
              </div>

              {/* Client Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Nombre Cliente *</label>
                  <input
                    type="text"
                    value={formData.leadName}
                    onChange={(e) => setFormData({ ...formData, leadName: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Empresa</label>
                  <input
                    type="text"
                    value={formData.company}
                    onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Teléfono</label>
                  <input
                    type="text"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2"
                  />
                </div>
              </div>

              {/* Services */}
              <div className="border-t border-slate-100 pt-4">
                <label className="block text-sm font-medium text-slate-700 mb-2">Servicios Contratados</label>
                <select
                  onChange={(e) => { addService(e.target.value); e.target.value = ''; }}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 bg-white mb-3"
                >
                  <option value="">+ Agregar servicio</option>
                  {services.filter(s => !formData.services.find(fs => fs.serviceId === s.id)).map(s => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>

                {formData.services.length > 0 && (
                  <div className="space-y-2">
                    {formData.services.map((service, index) => (
                      <div key={index} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                        <span className="flex-1 font-medium text-sm">{service.name}</span>
                        <input
                          type="text"
                          inputMode="numeric"
                          value={formatNumberAsCurrency(service.value)}
                          onChange={(e) => updateServiceValue(index, 'value', e.target.value)}
                          className="w-32 px-3 py-1 border border-slate-200 rounded text-sm text-right"
                          placeholder="Valor"
                        />
                        <input
                          type="number"
                          value={service.quantity}
                          onChange={(e) => updateServiceValue(index, 'quantity', e.target.value)}
                          className="w-20 px-3 py-1 border border-slate-200 rounded text-sm"
                          placeholder="Cant"
                          min="1"
                        />
                        <button onClick={() => removeService(index)} className="p-1 hover:bg-red-100 rounded">
                          <X className="w-4 h-4 text-red-500" />
                        </button>
                      </div>
                    ))}
                    <div className="flex justify-end pt-2">
                      <p className="text-lg font-bold" style={{ color: primaryColor }}>
                        Total: {new Intl.NumberFormat('es-CO').format(formData.totalValue)} COP
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Dates */}
              <div className="grid grid-cols-3 gap-4 border-t border-slate-100 pt-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Inicio Estimado</label>
                  <input
                    type="date"
                    value={formData.estimatedStartDate}
                    onChange={(e) => setFormData({ ...formData, estimatedStartDate: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Cierre Estimado</label>
                  <input
                    type="date"
                    value={formData.estimatedCloseDate}
                    onChange={(e) => setFormData({ ...formData, estimatedCloseDate: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Estado</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 bg-white"
                  >
                    <option value="negotiation">En Negociación</option>
                    <option value="won">Ganado</option>
                    <option value="lost">Perdido</option>
                  </select>
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Notas</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 resize-none"
                />
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 p-6 border-t border-slate-200 sticky bottom-0 bg-white">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 text-slate-700 bg-slate-100 rounded-lg hover:bg-slate-200"
              >
                Cancelar
              </button>
              <button
                onClick={handleSubmit}
                className="flex items-center gap-2 px-4 py-2 text-white rounded-lg hover:opacity-90"
                style={{ backgroundColor: primaryColor }}
              >
                <Save className="w-4 h-4" />
                {editingDeal ? 'Actualizar' : 'Crear Negocio'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-sm">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-red-100 rounded-full">
                <AlertCircle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900">¿Eliminar negocio?</h3>
                <p className="text-sm text-slate-500">Esta acción no se puede deshacer</p>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteConfirm(null)}
                className="flex-1 px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200"
              >
                Cancelar
              </button>
              <button
                onClick={() => handleDelete(showDeleteConfirm)}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Deals;
